import { Link } from "react-router-dom";
import { Compass, ChevronDown, Gamepad2, Briefcase, UserRound } from "lucide-react";
import { useEffect, useState } from "react";

/* -----------------------------------------------------------
   PublicChrome — Header/Footer partagés des pages publiques
   Identité "Aventure & Temps" : noir · or · parchemin · vert
   ----------------------------------------------------------- */

export function PublicHeader({ current = null }) {
  const linkBase =
    "text-sm tracking-wide transition-colors duration-300";
  const linkIdle = "text-parchemin/70 hover:text-or";
  const linkActive = "text-or";

  const links = [
    { to: "/", label: "Accueil", key: "home" },
    { to: "/reserver", label: "Réserver", key: "reserver" },
    { to: "/producteurs", label: "Producteurs", key: "producteurs" },
    { to: "/galerie", label: "Galerie", key: "galerie" },
  ];

  return (
    <header
      className="sticky top-0 z-40 bg-noir/85 backdrop-blur-md border-b border-or/15"
      data-testid="public-header"
    >
      <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
        <Link
          to="/"
          className="flex items-center gap-2 group"
          data-testid="nav-logo"
        >
          <Compass className="w-5 h-5 text-or group-hover:rotate-45 transition-transform duration-500" />
          <span className="font-display text-lg text-parchemin tracking-wide">
            Picnic<span className="text-or">·</span>Game
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <Link
              key={l.key}
              to={l.to}
              data-testid={`nav-${l.key}`}
              className={`${linkBase} ${
                current === l.key ? linkActive : linkIdle
              }`}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <EspaceMenu active={current === "auth" || current === "jouer"} />
      </div>
    </header>
  );
}

/* -----------------------------------------------------------
   EspaceMenu — Bouton "Espace" + popover Joueur / Pro
   Un seul point d'entrée qui laisse le visiteur choisir.
   ----------------------------------------------------------- */
export function EspaceMenu({ active = false }) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <>
      <div className="relative">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          data-testid="nav-espace-btn"
          aria-haspopup="menu"
          aria-expanded={open}
          className={`relative z-50 inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest px-4 py-2 border transition-all duration-300 ${
            active || open
              ? "bg-or/10 border-or text-or"
              : "border-or/30 hover:border-or hover:bg-or/5 text-or"
          }`}
        >
          Espace
          <ChevronDown
            className={`w-3.5 h-3.5 transition-transform duration-300 ${
              open ? "rotate-180" : ""
            }`}
          />
        </button>

        {open && (
          <div
            role="menu"
            data-testid="espace-menu"
            className="absolute right-0 mt-2 w-72 z-50 origin-top-right border border-or/30 bg-noir/95 backdrop-blur-md rounded-sm shadow-[0_20px_60px_-20px_rgba(0,0,0,0.9)] overflow-hidden"
          >
            <div className="px-4 pt-3 pb-2 border-b border-or/15">
              <p className="font-mono text-[9px] tracking-[0.25em] uppercase text-or/60">
                Qui êtes-vous ?
              </p>
            </div>
            <EspaceMenuItem
              to="/jouer"
              testId="espace-menu-joueur"
              icon={Gamepad2}
              title="Joueur"
              sub="J'ai un code, je pars à l'aventure"
              onClick={() => setOpen(false)}
            />
            <div className="h-px bg-or/10 mx-2" />
            <EspaceMenuItem
              to="/compte"
              testId="espace-menu-compte"
              icon={UserRound}
              title="Mon compte"
              sub="Mes réservations & mes codes d'accès"
              onClick={() => setOpen(false)}
            />
            <div className="h-px bg-or/10 mx-2" />
            <EspaceMenuItem
              to="/auth"
              testId="espace-menu-pro"
              icon={Briefcase}
              title="Professionnel"
              sub="Back-office, sessions live, parcours"
              onClick={() => setOpen(false)}
            />
          </div>
        )}
      </div>

      {open && (
        <button
          type="button"
          aria-label="Fermer le menu"
          data-testid="espace-menu-backdrop"
          onClick={() => setOpen(false)}
          className="fixed inset-0 z-40 cursor-default bg-transparent"
          tabIndex={-1}
        />
      )}
    </>
  );
}

function EspaceMenuItem({ to, testId, icon: Icon, title, sub, onClick }) {
  return (
    <Link
      to={to}
      data-testid={testId}
      onClick={onClick}
      role="menuitem"
      className="flex items-start gap-3 px-4 py-3 hover:bg-or/5 transition-colors group"
    >
      <div className="mt-0.5 w-8 h-8 rounded-sm border border-or/25 bg-noir-100 flex items-center justify-center text-or shrink-0 group-hover:border-or/60 transition-colors">
        <Icon className="w-4 h-4" strokeWidth={1.6} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-display text-base text-parchemin group-hover:text-or transition-colors leading-tight">
          {title}
        </div>
        <div className="mt-0.5 text-[11px] text-parchemin/55 font-mono tracking-wide">
          {sub}
        </div>
      </div>
    </Link>
  );
}

export function PublicFooter() {
  return (
    <footer
      className="border-t border-or/15 bg-noir-100 py-10 px-6 mt-16"
      data-testid="public-footer"
    >
      <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6 text-parchemin/50 text-xs">
        <div className="flex items-center gap-2">
          <Compass className="w-3.5 h-3.5 text-or/70" />
          <span className="font-display tracking-wide">
            Picnic<span className="text-or">·</span>Game — Lot &amp; Célé
          </span>
        </div>
        <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
          <Link
            to="/legal/mentions-legales"
            className="hover:text-or transition-colors"
            data-testid="footer-mentions"
          >
            Mentions légales
          </Link>
          <Link
            to="/legal/cgv"
            className="hover:text-or transition-colors"
            data-testid="footer-cgv"
          >
            CGV
          </Link>
          <Link
            to="/legal/confidentialite"
            className="hover:text-or transition-colors"
            data-testid="footer-conf"
          >
            Confidentialité
          </Link>
          <a
            href="mailto:contact@picnicgame.fr"
            className="hover:text-or transition-colors"
            data-testid="footer-contact"
          >
            contact@picnicgame.fr
          </a>
        </div>
        <div className="font-mono text-[10px] tracking-widest uppercase text-or/40">
          MMXXVI
        </div>
      </div>
    </footer>
  );
}

/* Badge "Chapitre X · Y" — accent narratif partagé */
export function ChapterBadge({ children, testId }) {
  return (
    <div
      className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-or/30 bg-noir-50/50 backdrop-blur-sm"
      data-testid={testId}
    >
      <Compass className="w-3.5 h-3.5 text-or" />
      <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-or/90">
        {children}
      </span>
    </div>
  );
}
